/**
 * Terminal screensaver: matrix-style rain in the current phosphor theme,
 * with the LAUREL banner drifting through it. Mounted by the Terminal
 * after a few minutes of idle (fullscreen variant only); the host
 * unmounts it on any activity.
 */

import { useEffect, useRef } from "react";
import { LAUREL_BANNER } from "./ascii";
import s from "./Terminal.module.css";

const GLYPHS = "01<>/{}[]()=+-*;:.#$%&@ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷ";

export function Screensaver() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Paint in whatever the active theme's phosphor color is.
    const style = getComputedStyle(canvas);
    const fg = style.getPropertyValue("--fg").trim() || "#33ff66";
    const bg = style.getPropertyValue("--bg").trim() || "#050a06";

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let cols = 0;
    let drops: number[] = [];
    const FONT = 14;

    const resize = () => {
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(canvas.clientWidth / FONT);
      drops = Array.from({ length: cols }, () => Math.random() * -50);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    };
    resize();
    window.addEventListener("resize", resize);

    // The banner bounces DVD-logo style, drawn as text so it stays crisp.
    const bannerLines = LAUREL_BANNER.split("\n").filter((l) => l.trim());
    const bannerW = Math.max(...bannerLines.map((l) => l.length)) * 7;
    const bannerH = bannerLines.length * 12;
    let bx = 40, by = 60, bdx = 0.6, bdy = 0.45;

    let last = 0;
    const frame = (t: number) => {
      raf = requestAnimationFrame(frame);
      if (t - last < 50) return; // ~20fps is plenty for rain
      last = t;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      // Fade the previous frame for trails.
      ctx.fillStyle = bg;
      ctx.globalAlpha = 0.18;
      ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = 1;

      ctx.font = `${FONT}px "JetBrains Mono", monospace`;
      for (let i = 0; i < cols; i++) {
        const ch = GLYPHS[(Math.random() * GLYPHS.length) | 0];
        const y = drops[i] * FONT;
        ctx.fillStyle = fg;
        ctx.globalAlpha = 0.85;
        ctx.fillText(ch, i * FONT, y);
        ctx.globalAlpha = 1;
        drops[i] = y > h && Math.random() > 0.975 ? 0 : drops[i] + 1;
      }

      // Bouncing banner.
      bx += bdx * 50;
      by += bdy * 50;
      if (bx < 0 || bx + bannerW > w) bdx *= -1;
      if (by < bannerH || by + bannerH > h) bdy *= -1;
      bx = Math.max(0, Math.min(bx, Math.max(0, w - bannerW)));
      by = Math.max(bannerH, Math.min(by, Math.max(bannerH, h - bannerH)));
      ctx.font = '11px "JetBrains Mono", monospace';
      ctx.fillStyle = fg;
      bannerLines.forEach((line, i) => ctx.fillText(line, bx, by + i * 12));

      ctx.font = `12px "JetBrains Mono", monospace`;
      ctx.globalAlpha = 0.5;
      ctx.fillText("— press any key —", w / 2 - 60, h - 24);
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={s.screensaver} aria-hidden />;
}
